/**
 * Sign-off timestamps.
 *
 * A signer can be in a different timezone than the project site, so we
 * capture BOTH the UTC instant and the signer's local wall-clock time
 * plus zone, then display the local time with the UTC equivalent
 * underneath.
 */
import { dateLocale } from './dateFormat';

export const captureSignTime = () => {
  const now = new Date();
  const offsetMin = -now.getTimezoneOffset(); // minutes east of UTC
  const sign = offsetMin >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMin);
  const pad = (n) => String(n).padStart(2, '0');
  let zoneName = '';
  try {
    zoneName = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(now).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch (_e) { zoneName = ''; }
  return {
    signed_at: now.toISOString(),
    signed_tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    signed_tz_label: zoneName,
    signed_tz_offset_minutes: offsetMin,
    signed_at_local: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} `
      + `${pad(now.getHours())}:${pad(now.getMinutes())}`,
    signed_utc_offset: `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`,
  };
};

/** Local wall-clock line, e.g. "2026-05-29 14:32 (CEST, UTC+02:00)". */
export const formatSignedLocal = (so) => {
  if (!so) return '';
  if (so.signed_at_local) {
    const zone = [so.signed_tz_label, so.signed_utc_offset].filter(Boolean).join(', ');
    return zone ? `${so.signed_at_local} (${zone})` : so.signed_at_local;
  }
  if (!so.signed_at) return '';
  const d = new Date(so.signed_at);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(dateLocale());
};

/** UTC equivalent, shown small under the local time. */
export const formatSignedUtc = (so) => {
  if (!so?.signed_at) return '';
  const d = new Date(so.signed_at);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} `
    + `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
};
